/**
 * PGN parsing — walks a PGN game and extracts per-move data including
 * clock annotations. Produces MoveData[] ready for Stockfish evaluation.
 */
import { parseGamePgnTimeline } from "@chessla/shared";
import type { MoveData } from "./types";

const clockSeconds = (comment: string | null): number | null => {
  // eslint-disable-next-line security/detect-unsafe-regex -- Numeric fields are bounded and separated by literal delimiters.
  const match = /\[%clk\s+(\d{1,6}):([0-5]?\d):([0-5]?\d(?:\.\d{1,3})?)\]/.exec(comment ?? "");
  return match ? Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]) : null;
};

export function parsePgnMoves(pgn: string, playerColor: "w" | "b"): MoveData[] {
  const game = parseGamePgnTimeline(pgn);
  if (!game) throw new Error("Invalid PGN");

  // Only single-stage base+increment controls provide known elapsed time.
  // eslint-disable-next-line security/detect-unsafe-regex -- Bounded numeric fields separated by a literal plus.
  const timeControl = /^(\d{1,9})(?:\+(\d{1,9}))?$/.exec(game.headers.TimeControl ?? "");
  const incrementSeconds = timeControl ? Number(timeControl[2] ?? 0) : null;
  const baseSeconds = timeControl && !game.headers.FEN ? Number(timeControl[1]) : null;

  const movesData: MoveData[] = [];
  let lastWhiteClock: number | null = baseSeconds != null && Number.isFinite(baseSeconds) ? baseSeconds : null;
  let lastBlackClock: number | null = baseSeconds != null && Number.isFinite(baseSeconds) ? baseSeconds : null;

  for (const move of game.moves) {
    const { fenBefore, fen: fenAfter, san, uci: playedUci } = move;
    const from = playedUci.slice(0, 2);
    const to = playedUci.slice(2, 4);

    const sideToMove = fenBefore.split(" ")[1];
    const isPlayerMove = sideToMove === playerColor;
    const moveNumber = Number(fenBefore.split(" ")[5]);

    // Extract %clk from PGN comment if present (Chess.com always includes one).
    const moveClockSeconds = clockSeconds(move.comment);

    const isWhiteMove = sideToMove === "w";
    const priorClock = isWhiteMove ? lastWhiteClock : lastBlackClock;
    let timeSpentSeconds =
      moveClockSeconds != null && priorClock != null && incrementSeconds != null
        ? priorClock - moveClockSeconds + incrementSeconds
        : null;
    if (timeSpentSeconds != null && timeSpentSeconds < 0) timeSpentSeconds = null;

    if (isWhiteMove) lastWhiteClock = moveClockSeconds;
    else lastBlackClock = moveClockSeconds;

    movesData.push({
      san,
      fenBefore,
      fenAfter,
      moveNumber,
      isPlayerMove,
      playedFrom: from,
      playedTo: to,
      playedUci,
      clockSeconds: moveClockSeconds,
      timeSpentSeconds,
    });
  }

  return movesData;
}
